import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ui } from '@procuros/datachecks';

import { ErrorsNavigationContextProvider } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContextProvider';
import { getCanonical } from 'services/datachecks/datachecks';
import { logError } from 'services/logging/logging';
import { TaskErrorsServiceSingleton } from 'services/taskErrors';
import { useTaskErrors } from 'services/taskErrors/hooks';
import { TaskError } from 'services/taskErrors/types';
import { useInitTaskStore } from 'services/tasks/taskStore';
import { useTaskStore } from 'stores/tasks/tasks';
import { datachecksResolver } from 'support/helpers/resolvers/datachecksResolver';
import { ProcessSpecificationDTO, TaskBaseDTO, TaskDTO } from 'support/types';
import { DisplayModes } from 'components/DataTable/Types';
import { Form } from 'components/Form/Form';
import { SchemableFormVariablesContextProvider } from 'components/SchemableForm/contexts/SchemableFormVariablesContext/SchemableFormVariablesContextProvider';
import { SchemableForm } from 'components/SchemableForm/SchemableForm';
import { FileUploadWrapper } from 'components/TaskForm/components/FileUploadWrapper';
import { TaskReactiveUiConfig } from 'components/TaskForm/components/TaskReactiveUiConfig';
import { useInitialTaskSetup } from 'components/TaskForm/hooks/useInitialTaskSetup';

export const TASK_FORM_ID = 'task-form';

type TaskFormProps = {
  task: TaskBaseDTO | TaskDTO;
  mode: DisplayModes;
  isSubmitting: boolean;
  variables: Record<string, string>;
  relationshipId?: string;
  onSubmit?: (canonicalData: Record<string, unknown>) => void;
};

const ROOT_FIELD: NonNullable<TaskBaseDTO['actions']>[number] = {
  path: '$',
  name: '',
  description: '',
  type: 'object',
  modality: {
    type: 'MANDATORY',
    default: 'MANDATORY',
  },
  options: {
    display: {
      format: {
        value: 'object',
      },
    },
  },
};

const EMPTY_DATA = {};
export const TaskForm = ({ task, mode, isSubmitting, variables, relationshipId, onSubmit }: TaskFormProps) => {
  useInitTaskStore();

  const response = 'response' in task ? task.response || EMPTY_DATA : EMPTY_DATA;

  const actions = useMemo(() => {
    if (!task.actions) {
      return undefined;
    }
    return [ROOT_FIELD, ...task.actions];
  }, [task.actions]);

  const resolver = useMemo(
    () =>
      datachecksResolver({
        fieldsConfiguration: actions as ProcessSpecificationDTO['fields'] | undefined,
        withTimeout: true,
      }),
    [actions],
  );

  const formMethods = useForm({ resolver, reValidateMode: 'onChange' });

  const onFormSubmit = useCallback(async () => {
    if (!onSubmit || !actions) {
      return;
    }

    try {
      const fields = actions as ProcessSpecificationDTO['fields'];
      const { canonical } = await getCanonical(fields, formMethods.getValues());
      onSubmit(canonical);
    } catch (error) {
      logError(error);
    }
  }, [onSubmit, actions, formMethods]);

  return (
    <FormProvider {...formMethods}>
      <Form id={TASK_FORM_ID} onSubmit={formMethods.handleSubmit(onFormSubmit)}>
        <TaskFormInner
          initialData={response}
          actions={actions}
          mode={mode}
          isSubmitting={isSubmitting}
          variables={variables}
          relationshipId={relationshipId}
          task={task}
        />
      </Form>
    </FormProvider>
  );
};

type TaskFormInnerProps = {
  task: TaskBaseDTO;
  initialData: Record<string, unknown> | undefined;
  actions: NonNullable<TaskBaseDTO['actions']> | undefined;
  mode: DisplayModes;
  isSubmitting: boolean;
  variables: Record<string, string>;
  relationshipId?: string;
};

const TaskFormInner = ({
  task,
  initialData,
  actions,
  mode,
  isSubmitting,
  variables,
  relationshipId,
}: TaskFormInnerProps) => {
  useInitialTaskSetup({ actions, initialData });
  const uiConfig = useTaskStore((state) => state.uiConfig);
  const isLoading = useTaskStore((state) => state.isLoading);

  useTaskErrors(variables);

  if (isLoading || uiConfig.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-6">
      <TaskReactiveUiConfig actions={actions} />
      <ErrorsNavigationContextProvider<TaskError> service={TaskErrorsServiceSingleton} ErrorModalComponent={null}>
        <FileUploadWrapper relationshipId={relationshipId} taskId={task.id}>
          <SchemableFormVariablesContextProvider variables={variables}>
            {uiConfig.map((section) => {
              if (section.type !== 'info') {
                return null;
              }
              return (
                <SchemableForm
                  key={section.type}
                  dataPathPrefix={`${section.type}.0`}
                  config={section as ui.InfoSection}
                  mode={mode}
                  isSubmitting={isSubmitting}
                  variant="generic"
                />
              );
            })}
          </SchemableFormVariablesContextProvider>
        </FileUploadWrapper>
      </ErrorsNavigationContextProvider>
    </section>
  );
};
